import { apiClient } from "@/lib/api-client";
import type {
  CatalogOrderSummary,
  CatalogProduct,
  ShoppingResultsResponse,
  ShoppingPreparation,
  CatalogSelection,
  ShoppingWalletStatus,
} from "@/types/shoppingCatalog.ts";
import { queryOptions } from "@tanstack/react-query";

// Catalog payloads are transient, including when the app persists other queries.
const transient = {
  gcTime: 0,
  staleTime: 0,
  retry: false,
  meta: { persist: false },
  refetchOnWindowFocus: "always" as const,
};
/** Evaluate shopping for the authenticated account; app Statsig SDKs identify the person. */
export const shoppingAccessQuery = () =>
  queryOptions({
    queryKey: ["shopping", "access"],
    ...transient,
    queryFn: async ({ signal }) =>
      (
        await apiClient.get<{ enabled: boolean; paymentsEnabled: boolean }>(
          "/api/a/shopping/access",
          { signal },
        )
      ).data,
  });
export const shoppingWalletQuery = () =>
  queryOptions({
    queryKey: ["shopping", "wallet"],
    ...transient,
    queryFn: async ({ signal }) =>
      (await apiClient.get<ShoppingWalletStatus>("/api/a/shopping/wallet", { signal })).data,
    refetchInterval: 10000,
  });
export const shoppingResultsQuery = (artifactId: string) =>
  queryOptions({
    queryKey: ["shopping", "results", artifactId],
    ...transient,
    queryFn: async ({ signal }) =>
      (
        await apiClient.get<ShoppingResultsResponse>(
          `/api/a/shopping/results/${encodeURIComponent(artifactId)}`,
          { signal },
        )
      ).data,
  });
export const shoppingOrderQuery = (orderId: string) =>
  queryOptions({
    queryKey: ["shopping", "order", orderId],
    ...transient,
    queryFn: async ({ signal }) =>
      (await apiClient.get<CatalogOrderSummary>(`/api/a/shopping/orders/${orderId}`, { signal }))
        .data,
    refetchInterval: 10000,
  });
export const shoppingProductQuery = (id: string, country?: string, currency?: string) =>
  queryOptions({
    queryKey: ["shopping", "product", id, country, currency],
    ...transient,
    queryFn: async ({ signal }) =>
      (
        await apiClient.post<CatalogProduct>(
          "/api/a/shopping/product",
          { id, country, currency },
          { signal },
        )
      ).data,
  });
export async function saveShoppingSelection(artifactId: string, selection: CatalogSelection) {
  await apiClient.post(
    `/api/a/shopping/results/${encodeURIComponent(artifactId)}/selection`,
    selection,
  );
}
export async function createShoppingCheckout(
  artifactId: string,
  selection: CatalogSelection,
  requestId: string,
) {
  return (
    await apiClient.post<
      { kind: "order"; order: CatalogOrderSummary } | { kind: "merchant"; url: string }
    >("/api/a/shopping/orders", { artifactId, ...selection, requestId })
  ).data;
}
export async function prepareShoppingCheckout(orderId: string) {
  return (
    await apiClient.post<CatalogOrderSummary>(`/api/a/shopping/orders/${orderId}/prepare`, {})
  ).data;
}
export async function changeShoppingTracking(orderId: string, enabled: boolean) {
  return (
    await apiClient.post<CatalogOrderSummary>(`/api/a/shopping/orders/${orderId}/tracking`, {
      enabled,
    })
  ).data;
}
export const shoppingWalletLabel = (status: ShoppingWalletStatus["status"]) =>
  ({
    not_connected: "Connect Link",
    connecting: "Finish connecting",
    needs_attention: "Reconnect Link",
    connected: "Link connected",
  })[status];

export const shipmentLabels = {
  labelCreated: "Shipping label created",
  shipped: "Shipped",
  outForDelivery: "Out for delivery",
  delivered: "Delivered",
  exception: "Delivery exception",
  unknown: "Awaiting shipping details",
};

export const shoppingStatusLabels = {
  selected: "Selection saved",
  preparing: "Preparing checkout for your review…",
  awaitingApproval: "Ready for your review. Payment requires your approval.",
  processing: "Checking your purchase…",
  confirmed: "Order confirmed",
  cancelled: "Payment not completed",
  needsAttention: "This purchase needs your attention.",
  outcomeUnknown: "Payment outcome is uncertain. Do not buy again while this is checked.",
};

export async function stopShoppingOrder(orderId: string) {
  return (await apiClient.post<CatalogOrderSummary>(`/api/a/shopping/orders/${orderId}/stop`, {}))
    .data;
}

export async function resolveShoppingVariant(input: {
  productId: string;
  merchantDomain: string;
  selected: Array<{ name: string; label: string }>;
  country: string;
  currency: string;
}) {
  const product = (
    await apiClient.post<CatalogProduct>("/api/a/shopping/product", {
      id: input.productId,
      selected: input.selected,
      country: input.country,
      currency: input.currency,
    })
  ).data;
  const variant = product.variants.find(
    (candidate) =>
      candidate.available &&
      candidate.merchantDomain === input.merchantDomain &&
      input.selected.every((option) =>
        candidate.options.some(
          (value) => value.name === option.name && value.label === option.label,
        ),
      ),
  );
  if (product.id !== input.productId || !variant)
    throw new Error("This combination is unavailable from this merchant.");
  return variant;
}

export async function prepareShoppingRecommendation(
  artifactId: string,
  threadId: number,
  option: number,
) {
  return (
    await apiClient.post<ShoppingPreparation>("/api/a/shopping/recommendations/prepare", {
      artifactId,
      threadId,
      option,
    })
  ).data;
}
